import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  UserPlus, Search, Calendar, Star, Heart, Coins, 
  CreditCard, CheckCircle, ArrowRight, Users, Compass,
  Gift, Shield, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const HowItWorksPage = () => {
  const { i18n } = useTranslation();

  const travelerSteps = [
    {
      icon: UserPlus,
      title: i18n.language === 'fr' ? 'Inscrivez-vous gratuitement' : 'Sign up for free',
      description: i18n.language === 'fr' 
        ? 'Créez votre compte en quelques clics. C\'est gratuit et sans engagement.'
        : 'Create your account in a few clicks. It\'s free and no commitment.'
    },
    {
      icon: Search,
      title: i18n.language === 'fr' ? 'Explorez les expériences' : 'Explore experiences',
      description: i18n.language === 'fr'
        ? 'Parcourez des centaines d\'expériences authentiques proposées par des locaux passionnés.'
        : 'Browse hundreds of authentic experiences offered by passionate locals.'
    },
    {
      icon: Calendar,
      title: i18n.language === 'fr' ? 'Réservez en toute simplicité' : 'Book with ease',
      description: i18n.language === 'fr'
        ? 'Choisissez votre date, payez en ligne de manière sécurisée et recevez votre confirmation.'
        : 'Choose your date, pay securely online, and receive your confirmation.'
    },
    {
      icon: Star,
      title: i18n.language === 'fr' ? 'Vivez l\'aventure' : 'Live the adventure',
      description: i18n.language === 'fr'
        ? 'Rencontrez votre guide et vivez une expérience inoubliable. Laissez un avis ensuite !'
        : 'Meet your guide and live an unforgettable experience. Leave a review afterwards!'
    }
  ];

  const guideSteps = [
    {
      icon: UserPlus,
      title: i18n.language === 'fr' ? 'Inscrivez-vous gratuitement' : 'Sign up for free',
      description: i18n.language === 'fr'
        ? 'Créez votre profil de guide. Ajoutez votre bio, vos compétences et vos photos.'
        : 'Create your guide profile. Add your bio, skills, and photos.'
    },
    {
      icon: Compass,
      title: i18n.language === 'fr' ? 'Créez vos expériences' : 'Create your experiences',
      description: i18n.language === 'fr'
        ? 'Décrivez ce que vous proposez, fixez vos prix (ou proposez gratuitement), ajoutez des photos.'
        : 'Describe what you offer, set your prices (or offer for free), add photos.'
    },
    {
      icon: Calendar,
      title: i18n.language === 'fr' ? 'Recevez des réservations' : 'Receive bookings',
      description: i18n.language === 'fr'
        ? 'Les voyageurs vous contactent et réservent. Vous êtes notifié par email.'
        : 'Travelers contact and book you. You\'re notified by email.'
    },
    {
      icon: CreditCard,
      title: i18n.language === 'fr' ? 'Soyez payé' : 'Get paid',
      description: i18n.language === 'fr'
        ? 'Pour les expériences payantes, recevez votre paiement directement via Stripe.'
        : 'For paid experiences, receive your payment directly via Stripe.'
    }
  ];

  return (
    <main className="min-h-screen pt-20 pb-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            {i18n.language === 'fr' ? 'Comment ça marche ?' : 'How does it work?'}
          </motion.h1>
          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            {i18n.language === 'fr' 
              ? 'XPerience connecte des voyageurs curieux avec des guides locaux passionnés. Découvrez comment rejoindre l\'aventure.'
              : 'XPerience connects curious travelers with passionate local guides. Discover how to join the adventure.'}
          </motion.p>
        </div>
      </section>

      {/* For Travelers */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Users className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">
                {i18n.language === 'fr' ? 'Pour les voyageurs' : 'For Travelers'}
              </h2>
              <p className="text-muted-foreground">
                {i18n.language === 'fr' ? 'Vivez des expériences authentiques' : 'Live authentic experiences'}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {travelerSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-6 border shadow-sm h-full">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold mb-4">
                    {index + 1}
                  </div>
                  <step.icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
                {index < travelerSteps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-3 w-6 h-6 text-gray-300 z-10" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Subscription Info for Travelers */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-10 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Gift className="w-8 h-8 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2">
                  {i18n.language === 'fr' ? 'Accédez aux expériences gratuites' : 'Access free experiences'}
                </h3>
                <p className="text-muted-foreground mb-3">
                  {i18n.language === 'fr' 
                    ? 'Certains guides passionnés proposent des expériences gratuites. Pour y accéder, souscrivez à notre abonnement annuel de 49€ et profitez d\'un accès illimité à toutes ces expériences pendant un an.'
                    : 'Some passionate guides offer free experiences. To access them, subscribe to our annual membership for €49 and enjoy unlimited access to all these experiences for a year.'}
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-1 text-sm text-purple-700">
                    <CheckCircle className="w-4 h-4" />
                    {i18n.language === 'fr' ? 'Accès illimité' : 'Unlimited access'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm text-purple-700">
                    <CheckCircle className="w-4 h-4" />
                    {i18n.language === 'fr' ? '49€/an' : '€49/year'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm text-purple-700">
                    <CheckCircle className="w-4 h-4" />
                    {i18n.language === 'fr' ? 'Annulez à tout moment' : 'Cancel anytime'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* For Guides */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
              <Compass className="w-7 h-7 text-orange-600" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">
                {i18n.language === 'fr' ? 'Pour les guides' : 'For Guides'}
              </h2>
              <p className="text-muted-foreground">
                {i18n.language === 'fr' ? 'Partagez votre passion' : 'Share your passion'}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {guideSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-6 border shadow-sm h-full">
                  <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold mb-4">
                    {index + 1}
                  </div>
                  <step.icon className="w-8 h-8 text-orange-500 mb-3" />
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
                {index < guideSteps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-3 w-6 h-6 text-gray-300 z-10" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Guide Options */}
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {/* Paid Experiences */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl p-6 border"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Coins className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold">
                    {i18n.language === 'fr' ? 'Expériences payantes' : 'Paid Experiences'}
                  </h3>
                  <span className="text-sm text-green-600 font-medium">
                    {i18n.language === 'fr' ? 'Inscription gratuite' : 'Free registration'}
                  </span>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {i18n.language === 'fr' ? 'Fixez vos propres prix' : 'Set your own prices'}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {i18n.language === 'fr' ? 'Recevez les paiements via Stripe' : 'Receive payments via Stripe'}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {i18n.language === 'fr' ? 'Commission de 15% par réservation' : '15% commission per booking'}
                </li>
              </ul>
            </motion.div>

            {/* Free Experiences */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-2xl p-6 border"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold">
                    {i18n.language === 'fr' ? 'Expériences gratuites' : 'Free Experiences'}
                  </h3>
                  <span className="text-sm text-green-600 font-medium">
                    {i18n.language === 'fr' ? 'Inscription gratuite' : 'Free registration'}
                  </span>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {i18n.language === 'fr' ? 'Partagez votre passion sans contrainte' : 'Share your passion freely'}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {i18n.language === 'fr' ? 'Badge "Guide Passionné" sur votre profil' : '"Passionate Guide" badge on your profile'}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {i18n.language === 'fr' ? 'Aucune commission, aucun frais' : 'No commission, no fees'}
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Events & Retreats Section - NEW */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-4xl block mb-4">🎪</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              {i18n.language === 'fr' ? 'Événements & Retraites' : 'Events & Retreats'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {i18n.language === 'fr'
                ? 'Organisez un festival, un marché artisanal ou une retraite bien-être ? Faites-le découvrir !'
                : 'Organizing a festival, artisan market, or wellness retreat? Get discovered!'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Event Organizers */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="bg-white rounded-2xl p-6 border shadow-sm"
            >
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <span className="text-2xl">🎭</span>
              </div>
              <h3 className="font-bold text-lg mb-2">
                {i18n.language === 'fr' ? 'Organisateurs d\'événements' : 'Event Organizers'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {i18n.language === 'fr'
                  ? 'Publiez vos festivals, concerts, marchés et touchez une audience de voyageurs curieux.'
                  : 'Publish your festivals, concerts, markets and reach curious travelers.'}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {i18n.language === 'fr' ? 'Publication gratuite' : 'Free listing'}
                </li>
                <li className="flex items-center gap-2 text-purple-600">
                  <Star className="w-4 h-4" />
                  {i18n.language === 'fr' ? 'Option "À la une" dès 20€' : '"Featured" option from 20€'}
                </li>
              </ul>
            </motion.div>

            {/* Therapists & Retreats */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 border shadow-sm"
            >
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <span className="text-2xl">🧘</span>
              </div>
              <h3 className="font-bold text-lg mb-2">
                {i18n.language === 'fr' ? 'Praticiens & Retraites' : 'Practitioners & Retreats'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {i18n.language === 'fr'
                  ? 'Proposez vos retraites yoga, méditation, développement personnel avec une visibilité maximale.'
                  : 'Offer your yoga, meditation, personal development retreats with maximum visibility.'}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {i18n.language === 'fr' ? 'Créez votre page retraite' : 'Create your retreat page'}
                </li>
                <li className="flex items-center gap-2 text-purple-600">
                  <Star className="w-4 h-4" />
                  {i18n.language === 'fr' ? 'Mise en avant sur la homepage' : 'Featured on homepage'}
                </li>
              </ul>
            </motion.div>

            {/* Guides & Local Events */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 border shadow-sm"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-4">
                <span className="text-2xl">🧭</span>
              </div>
              <h3 className="font-bold text-lg mb-2">
                {i18n.language === 'fr' ? 'Guides locaux' : 'Local Guides'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {i18n.language === 'fr'
                  ? 'Connectez les événements de votre région à vos expériences et proposez des offres combinées !'
                  : 'Connect local events to your experiences and offer combined packages!'}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {i18n.language === 'fr' ? 'Liez des événements à vos offres' : 'Link events to your offers'}
                </li>
                <li className="flex items-center gap-2 text-orange-600">
                  <Globe className="w-4 h-4" />
                  {i18n.language === 'fr' ? 'Devenez ambassadeur local' : 'Become a local ambassador'}
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Premium Options */}
          <div className="bg-white rounded-2xl p-8 border shadow-sm">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                  <Star className="w-6 h-6 text-yellow-500" />
                  {i18n.language === 'fr' ? 'Options de mise en avant' : 'Featured Options'}
                </h3>
                <p className="text-muted-foreground">
                  {i18n.language === 'fr'
                    ? 'Boostez la visibilité de votre événement ou retraite'
                    : 'Boost the visibility of your event or retreat'}
                </p>
              </div>
              <div className="flex gap-4">
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <span className="text-2xl font-bold text-purple-600">20€</span>
                  <p className="text-xs text-muted-foreground mt-1">Essentiel</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <span className="text-2xl font-bold text-orange-600">50€</span>
                  <p className="text-xs text-muted-foreground mt-1">Visibilité+</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl">
                  <span className="text-lg font-bold text-yellow-600">Sur devis</span>
                  <p className="text-xs text-muted-foreground mt-1">Premium</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              {i18n.language === 'fr' ? 'Confiance & Sécurité' : 'Trust & Safety'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {i18n.language === 'fr'
                ? 'Nous mettons tout en œuvre pour garantir des expériences sûres et authentiques.'
                : 'We do everything to ensure safe and authentic experiences.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: i18n.language === 'fr' ? 'Guides vérifiés' : 'Verified Guides',
                description: i18n.language === 'fr'
                  ? 'Les guides peuvent obtenir un badge "Vérifié" après validation de leur identité.'
                  : 'Guides can obtain a "Verified" badge after identity validation.'
              },
              {
                icon: Star,
                title: i18n.language === 'fr' ? 'Avis authentiques' : 'Authentic Reviews',
                description: i18n.language === 'fr'
                  ? 'Seuls les voyageurs ayant réservé peuvent laisser un avis.'
                  : 'Only travelers who have booked can leave a review.'
              },
              {
                icon: CreditCard,
                title: i18n.language === 'fr' ? 'Paiements sécurisés' : 'Secure Payments',
                description: i18n.language === 'fr'
                  ? 'Toutes les transactions sont sécurisées via Stripe.'
                  : 'All transactions are secured via Stripe.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <item.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-orange-500">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {i18n.language === 'fr' ? 'Prêt à commencer ?' : 'Ready to start?'}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {i18n.language === 'fr'
              ? 'Rejoignez notre communauté de voyageurs et guides passionnés.'
              : 'Join our community of travelers and passionate guides.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" variant="secondary" className="gap-2">
                {i18n.language === 'fr' ? 'Créer un compte' : 'Create an account'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/explore">
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10">
                {i18n.language === 'fr' ? 'Explorer les expériences' : 'Explore experiences'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HowItWorksPage;
